import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  DataTableComponent,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';

import {
  BARCODE_CORRECTIONS_SEARCH_CONFIG,
  BARCODE_CORRECTIONS_TABLE_COLUMNS,
  BARCODE_CORRECTIONS_DEFAULT_CONFIG,
  getBarcodeCorrectionStatusLabel,
  getBarcodeCorrectionStatusSeverity,
} from './barcode-corrections.config';
import {
  BarcodeCorrectionsService,
  BarcodeCorrectionsSearchParams,
} from './barcode-corrections.service';
import { BarcodeCorrectionLog } from '../../../../api/stock-service/model/models';

@Component({
  selector: 'app-barcode-corrections',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TagModule,
    TooltipModule,
    DataTableComponent,
    SearchFormComponent,
  ],
  templateUrl: './barcode-corrections.component.html',
  styleUrl: './barcode-corrections.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarcodeCorrectionsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  dataSource = signal<BarcodeCorrectionLog[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  currentSearchParams = signal<Partial<BarcodeCorrectionsSearchParams>>({});

  tableColumns = computed(() => BARCODE_CORRECTIONS_TABLE_COLUMNS);
  searchConfig = computed(() => BARCODE_CORRECTIONS_SEARCH_CONFIG);

  currentPage = 1;
  pageSize: number = BARCODE_CORRECTIONS_DEFAULT_CONFIG.PAGE_SIZE;

  constructor(
    private readonly barcodeCorrectionsService: BarcodeCorrectionsService,
  ) {}

  ngOnInit(): void {
    this.loadBarcodeCorrections();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBarcodeCorrections(): void {
    this.loading.set(true);
    const searchParams = this.currentSearchParams();
    const params: BarcodeCorrectionsSearchParams = {
      ...searchParams,
      page: this.currentPage - 1,
      size: this.pageSize,
    };

    this.barcodeCorrectionsService
      .getBarcodeCorrections(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.data);
          this.totalRecords.set(response.total);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading barcode correction logs:', error);
          this.loading.set(false);
        },
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.currentSearchParams.set({
      scannedBarcode: searchData['scannedBarcode'] || undefined,
      skuCode: searchData['skuCode'] || undefined,
      warehouseWarrantCode: searchData['warehouseWarrantCode'] || undefined,
      status: searchData['status'] || undefined,
      createdBy: searchData['createdBy'] || undefined,
      fromDate: searchData['fromDate'] || undefined,
      toDate: searchData['toDate'] || undefined,
    });
    this.currentPage = 1;
    this.loadBarcodeCorrections();
  }

  onReset(): void {
    this.currentSearchParams.set({});
    this.currentPage = 1;
    this.loadBarcodeCorrections();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || BARCODE_CORRECTIONS_DEFAULT_CONFIG.PAGE_SIZE;
    this.loadBarcodeCorrections();
  }

  getBarcodeCorrectionStatusLabel(status: string): string {
    return getBarcodeCorrectionStatusLabel(status);
  }

  getBarcodeCorrectionStatusSeverity(
    status: string,
  ): 'success' | 'warning' | 'danger' | 'secondary' {
    return getBarcodeCorrectionStatusSeverity(status);
  }
}
